import { Record } from 'immutable';
import { map, capitalize } from 'lodash';
import * as types from './actionTypes';
import constants from '../constants';

export const SEND_STICKER_ERROR_KEY = `send_sticker_${constants.ERROR_KEY}`;

const requestReducerState = Record({
  recentDealList: null,
  all: null,
  csv: null,
  deal: null,
  car: null,
  user: null,
  dealer: null,
  pencil: null,
  payments: null,
  pencilModalText: '',
  pencilError: null,
  selectedCars: [],
  [constants.ERROR_KEY]: null,
  [SEND_STICKER_ERROR_KEY]: null,
  [constants.FETCHING_KEY]: false,
  sendingSticker: false,
  pushingToCRM: false,
  pushingToDMS: false,
  isPencilsMutating: false,
});

/**
 * After we update a deal we want to merge the updated data
 * to the recent deal list, otherwise it will not get updated
 * until the next tick of polling.
 */
const mergeUpdated = updated => deal => {
  if (deal.id === updated.id) {
    return { ...deal, ...updated };
  }
  return deal;
};

const updateItemInList = (list, idx, partialUpdate) => [
  ...list.slice(0, idx),
  { ...list[idx], ...partialUpdate },
  ...list.slice(idx + 1, list.length),
];

export default (state = requestReducerState(), action) => {
  const { type, payload } = action;
  switch (type) {
    case types.RECENT_DEAL_LIST_SUCCESS:
      return state
        .set(constants.ERROR_KEY, null)
        .set(constants.FETCHING_KEY, false)
        .set('recentDealList', payload);
    case types.ALL_DEALS_SUCCESS:
      return state
        .set(constants.ERROR_KEY, null)
        .set(constants.FETCHING_KEY, false)
        .set('all', payload);
    case types.DEAL_SUCCESS:
    case types.DEAL_CREATE_SUCCESS:
    case types.DEAL_CRM_PUSH_SUCCESS:
    case types.ONLINE_DEAL_CREATE_SUCCESS:
      return state
        .set(constants.ERROR_KEY, null)
        .set(constants.FETCHING_KEY, false)
        .set('deal', payload);
    case types.CAR_SUCCESS:
      return state
        .set(constants.ERROR_KEY, null)
        .set(constants.FETCHING_KEY, false)
        .set('car', payload);
    case types.USER_SUCCESS:
      return state
        .set(constants.ERROR_KEY, null)
        .set(constants.FETCHING_KEY, false)
        .set('user', payload);
    case types.DEALER_SUCCESS:
      return state
        .set(constants.ERROR_KEY, null)
        .set(constants.FETCHING_KEY, false)
        .set('dealer', payload);
    case types.PENCIL_SUCCESS:
    case types.SET_PENCIL:
      return state
        .set(constants.ERROR_KEY, null)
        .set(constants.FETCHING_KEY, false)
        .set('pencil', payload);
    case types.PENCIL_CREATE_REQUEST:
    case types.PENCIL_UPDATE_REQUEST:
    case types.PENCIL_CRM_PUSH_REQUEST:
      return state
        .set(constants.ERROR_KEY, null)
        .set(constants.FETCHING_KEY, true)
        .set('pencilModalText', '')
        .set('isPencilsMutating', true);
    case types.PENCIL_CRM_PUSH_SUCCESS:
      return state
        .set(constants.FETCHING_KEY, false)
        .set(
          'pencilModalText',
          `All payment information has been pushed to ${capitalize(
            state.dealer.crmType,
          )}. CRM values might differ based on your CRM default lenders. Please verify the lender to match payments accurately.`,
        );
    case types.PENCIL_CREATE_SUCCESS:
    case types.PENCIL_UPDATE_SUCCESS:
      return state
        .set(constants.ERROR_KEY, null)
        .set(constants.FETCHING_KEY, false)
        .set('pencil', payload.pencil)
        .set('pencilModalText', payload.msg)
        .set('isPencilsMutating', false);
    case types.PENCIL_CREATE_FAILURE:
    case types.PENCIL_UPDATE_FAILURE:
      return state
        .set(constants.FETCHING_KEY, false)
        .set('pencilModalText', payload.msg)
        .set('isPencilsMutating', false);
    case types.PENCIL_CRM_PUSH_FAILURE:
      return state
        .set(constants.FETCHING_KEY, false)
        .set('pencilModalText', 'Failed to push to CRM. Please try again.');
    case types.PAYMENTS_SUCCESS:
      return state.set('payments', action.payload);
    case types.SHOWROOM_LIST_REQUEST:
    case types.RECENT_DEAL_LIST_REQUEST:
    case types.ALL_DEALS_REQUEST:
    case types.CAR_REQUEST:
    case types.USER_REQUEST:
    case types.DEAL_REQUEST:
    case types.DEAL_CREATE_REQUEST:
    case types.DEAL_CRM_PUSH_REQUEST:
    case types.SELECTED_CARS_REQUEST:
    case types.PRINT_STICKER_REQUEST:
    case types.ONLINE_DEAL_CREATE_REQUEST:
      return state
        .set(constants.ERROR_KEY, null)
        .set(constants.FETCHING_KEY, true);
    case types.PAYMENTS_REQUEST:
    case types.PAYMENTS_RESET:
      return state.set('payments', null);
    case types.SHOWROOM_LIST_FAILURE:
    case types.RECENT_DEAL_LIST_FAILURE:
    case types.ALL_DEALS_FAILURE:
    case types.CAR_FAILURE:
      return state.set(constants.ERROR_KEY, action.payload);
    case types.USER_FAILURE:
    case types.DEAL_FAILURE:
    case types.DEALER_FAILURE:
    case types.PENCIL_FAILURE:
    case types.PAYMENTS_FAILURE:
    case types.DEAL_TRADEINS_UPDATE_FAILURE:
    case types.DEAL_UPDATE_FAILURE:
    case types.DEAL_CRM_PUSH_FAILURE:
    case types.SELECTED_CARS_FAILURE:
    case types.PRINT_STICKER_FAILURE:
    case types.ONLINE_DEAL_CREATE_FAILURE:
      return state
        .set(constants.FETCHING_KEY, false)
        .set(constants.ERROR_KEY, action.payload);
    case types.SET_DEAL_TRADEINS:
      return state.set('deal', {
        ...state.get('deal'),
        tradeIns: payload.tradeIns,
      });
    case types.DEAL_TRADEINS_UPDATE_SUCCESS:
      return state
        .set(constants.ERROR_KEY, null)
        .set(constants.FETCHING_KEY, false);
    case types.DEAL_UPDATE_SUCCESS: {
      const recentDealList = map(
        state.get('recentDealList'),
        mergeUpdated(payload),
      );
      return state
        .set('recentDealList', recentDealList)
        .set(constants.ERROR_KEY, null)
        .set(constants.FETCHING_KEY, false)
        .set('deal', payload);
    }
    case types.SELECTED_CARS_SUCCESS:
      return state.set('selectedCars', action.payload);
    case types.SELECTED_CARS_UPDATE_CAR: {
      const carList = state.get('selectedCars');
      const carIdx = carList?.findIndex(c => c.id === action.payload.id);
      if (carIdx === -1 || carIdx === undefined) {
        return state;
      }
      return state.set(
        'selectedCars',
        updateItemInList(carList, carIdx, action.payload),
      );
    }
    case types.PRINT_STICKER_SUCCESS:
      return state.set(constants.FETCHING_KEY, false);
    case types.SEND_STICKER_REQUEST:
      return state
        .set(constants.ERROR_KEY, null)
        .set(SEND_STICKER_ERROR_KEY, null)
        .set('sendingSticker', true)
        .set('pencilModalText', '');
    case types.SEND_STICKER_SUCCESS:
      return state
        .set(constants.ERROR_KEY, null)
        .set(SEND_STICKER_ERROR_KEY, null)
        .set('sendingSticker', false)
        .set('pencil', payload);
    case types.SEND_STICKER_FAILURE: {
      let errorMessage = 'Failed to send deal. Please try again.';
      try {
        if (payload.error) {
          errorMessage = JSON.parse(payload.error).error;
        }
      } catch (e) {
        /* NOOP */
      }
      return state
        .set('sendingSticker', false)
        .set(SEND_STICKER_ERROR_KEY, errorMessage)
        .set('pencilModalText', errorMessage);
    }
    case types.FORWARD_TO_FNI_SUCCESS: {
      return state
        .set(constants.ERROR_KEY, null)
        .set('deal', payload)
        .set('pencilModalText', 'Credit app successfully submitted');
    }
    case types.FORWARD_TO_FNI_FAILURE: {
      return state.set(
        'pencilModalText',
        'An error occurred while submitting credit application',
      );
    }
    case types.PUSHING_TO_CRM:
      return state.set('pushingToCRM', action.payload);
    case types.PUSHING_TO_DMS:
      return state.set('pushingToDMS', action.payload);
    case types.DEAL_DMS_PUSH_REQUEST:
    case types.DEAL_DMS_PUSH_OFFER_REQUEST:
      return state.set(constants.ERROR_KEY, null).set('pencilModalText', '');
    case types.DEAL_DMS_PUSH_SUCCESS:
      return state
        .set(constants.ERROR_KEY, null)
        .set('deal', payload.deal)
        .set('pencilModalText', payload.msg);
    case types.DEAL_DMS_PUSH_OFFER_SUCCESS:
      return state
        .set(constants.ERROR_KEY, null)
        .set('pencilModalText', payload.msg);
    case types.DEAL_DMS_PUSH_FAILURE:
    case types.DEAL_DMS_PUSH_OFFER_FAILURE:
    case types.SET_PENCIL_MODAL_TEXT:
    case types.FINANCE_RESPONSE_UPDATE_FAILURE:
      return state.set('pencilModalText', payload);
    default:
      return state;
  }
};
